import React, { useState } from 'react';
import { DocumentTextIcon, XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Vehicle, VehicleDocument } from '../../types/vehicle';
import { doc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface VehicleDocumentUploaderProps {
  vehicle: Vehicle;
  onUploaded?: () => void;
}

const VehicleDocumentUploader: React.FC<VehicleDocumentUploaderProps> = ({ vehicle, onUploaded }) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<'registration' | 'insurance' | 'inspection' | 'other'>('registration');
  const [documentName, setDocumentName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('A fájl mérete maximum 10 MB lehet');
        return;
      }
      setSelectedFile(file);
      // Auto-fill document name from file name
      if (!documentName) {
        setDocumentName(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentName) {
      toast.error('Válassz fájlt és adj meg nevet');
      return;
    }

    setUploading(true);
    try {
      // Fájl feltöltése Storage-ba
      const fileName = `vehicles/${vehicle.id}/documents/${Date.now()}_${selectedFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, selectedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      // Dokumentum hozzáadása a járműhöz
      const document: Omit<VehicleDocument, 'id'> = {
        name: documentName,
        type: documentType,
        url: downloadUrl,
        uploadedAt: new Date(),
        size: selectedFile.size,
        ...(expiryDate && { expiryDate: new Date(expiryDate) })
      };

      await updateDoc(doc(db, 'vehicles', vehicle.id), {
        documents: arrayUnion({
          ...document,
          id: Date.now().toString()
        }),
        updatedAt: serverTimestamp()
      });

      toast.success('Dokumentum feltöltve');
      
      // Reset form
      setSelectedFile(null);
      setDocumentName('');
      setExpiryDate('');
      setDocumentType('registration');
      
      if (onUploaded) onUploaded();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels = {
      registration: 'Forgalmi engedély',
      insurance: 'Biztosítás',
      inspection: 'Műszaki vizsga',
      other: 'Egyéb'
    };
    return labels[type as keyof typeof labels] || type;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
        Dokumentum feltöltése
      </h3>

      <div className="space-y-4">
        {/* Dokumentum típus */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Dokumentum típusa *
          </label>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as any)}
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
          >
            <option value="registration">Forgalmi engedély</option>
            <option value="insurance">Biztosítás</option>
            <option value="inspection">Műszaki vizsga</option>
            <option value="other">Egyéb</option>
          </select>
        </div>

        {/* Dokumentum név */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Dokumentum neve *
          </label>
          <input
            type="text"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            placeholder="Pl. Forgalmi engedély 2024"
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        {/* Lejárati dátum (opcionális) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Lejárati dátum (opcionális)
          </label>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="block w-full pl-10 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Biztosítás vagy műszaki vizsga lejáratához
          </p>
        </div>

        {/* Fájl választás */}
        {!selectedFile ? (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label htmlFor="document-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-primary-600 hover:text-primary-500">
                  Válassz egy fájlt
                </span>
                <input
                  id="document-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileSelect}
                  className="sr-only"
                />
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                PDF, JPG, PNG, DOC max. 10MB
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <DocumentTextIcon className="w-8 h-8 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedFile(null)}
              className="p-1 text-red-600 hover:text-red-700 dark:text-red-400"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Feltöltés gomb */}
        <button
          onClick={handleUpload}
          disabled={uploading || !selectedFile || !documentName}
          className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Feltöltés...' : 'Dokumentum feltöltése'}
        </button>
      </div>

      {/* Meglévő dokumentumok */}
      {vehicle.documents && vehicle.documents.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Meglévő dokumentumok ({vehicle.documents.length})
          </h4>
          <div className="space-y-2">
            {vehicle.documents.map((doc) => (
              <a
                key={doc.id}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center flex-1">
                  <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {doc.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {getDocumentTypeLabel(doc.type)}
                      {doc.expiryDate && (
                        <span className="ml-2">
                          • Lejár: {(doc.expiryDate instanceof Date ? doc.expiryDate : new Date(doc.expiryDate as any)).toLocaleDateString('hu-HU')}
                        </span>
                      )}
                    </p>                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDocumentUploader;
